import React from 'react'
import clsx from 'clsx'
import moment from 'moment'
import { KTSVG } from '_metronic/helpers'

interface DateRangePickerProps {
  from: string
  to: string
  onChange: (from: string, to: string) => void
  placeholder?: string
  maxDate?: Date
  className?: string
}

const ISO = 'YYYY-MM-DD'
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const toIso = (value: moment.Moment) => value.format(ISO)
const display = (value: string) => (value ? moment(value, ISO).format('DD-MM-YYYY') : '')

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  from,
  to,
  onChange,
  placeholder = 'เลือกช่วงวันที่',
  maxDate,
  className,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [open, setOpen] = React.useState(false)
  const [viewMonth, setViewMonth] = React.useState(() =>
    moment(from || undefined, from ? ISO : undefined).startOf('month')
  )
  const [pendingStart, setPendingStart] = React.useState<string | null>(null)
  const [hoverDay, setHoverDay] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) return
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
        setPendingStart(null)
        setHoverDay(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const openPicker = () => {
    setViewMonth(moment(from || undefined, from ? ISO : undefined).startOf('month'))
    setPendingStart(null)
    setHoverDay(null)
    setOpen(true)
  }

  const maxIso = maxDate ? toIso(moment(maxDate)) : null

  const handleDayClick = (dayIso: string) => {
    if (!pendingStart) {
      setPendingStart(dayIso)
      setHoverDay(dayIso)
      return
    }

    let start = pendingStart
    let end = dayIso
    if (moment(end, ISO).isBefore(moment(start, ISO))) {
      ;[start, end] = [end, start]
    }
    onChange(start, end)
    setPendingStart(null)
    setHoverDay(null)
    setOpen(false)
  }

  const rangeStart = pendingStart ?? from
  const rangeEnd = pendingStart ? hoverDay ?? pendingStart : to

  const isInRange = (dayIso: string) => {
    if (!rangeStart || !rangeEnd) return false
    const a = moment.min(moment(rangeStart, ISO), moment(rangeEnd, ISO))
    const b = moment.max(moment(rangeStart, ISO), moment(rangeEnd, ISO))
    const day = moment(dayIso, ISO)
    return day.isSameOrAfter(a, 'day') && day.isSameOrBefore(b, 'day')
  }

  const isEdge = (dayIso: string) => dayIso === rangeStart || dayIso === rangeEnd

  const calendarDays = React.useMemo(() => {
    const start = viewMonth.clone().startOf('month').startOf('week')
    return Array.from({ length: 42 }, (_, i) => start.clone().add(i, 'day'))
  }, [viewMonth])

  const label =
    from && to
      ? `${display(from)} - ${display(to)}`
      : from
      ? display(from)
      : ''

  return (
    <div className='position-relative' ref={containerRef}>
      <div
        className={clsx('form-control form-control-solid d-flex align-items-center cursor-pointer', className)}
        onClick={() => (open ? setOpen(false) : openPicker())}>
        <KTSVG
          path='/media/icons/duotune/general/gen014.svg'
          className='svg-icon-2 text-muted me-2'
        />
        <span className={clsx('flex-grow-1 text-nowrap', !label && 'text-muted')}>
          {label || placeholder}
        </span>
      </div>

      {open && (
        <div
          className='position-absolute bg-body rounded shadow-sm border p-4 mt-2'
          style={{ zIndex: 1050, width: 300 }}>
          <div className='d-flex align-items-center justify-content-between mb-3'>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-light'
              onClick={() => setViewMonth(prev => prev.clone().subtract(1, 'month'))}
              aria-label='Previous month'>
              <KTSVG path='/media/icons/duotune/arrows/arr074.svg' className='svg-icon-2' />
            </button>
            <span className='fw-bold'>{viewMonth.format('MMMM YYYY')}</span>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-light'
              onClick={() => setViewMonth(prev => prev.clone().add(1, 'month'))}
              aria-label='Next month'>
              <KTSVG path='/media/icons/duotune/arrows/arr071.svg' className='svg-icon-2' />
            </button>
          </div>

          <div className='d-grid' style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {WEEKDAYS.map(weekday => (
              <div key={weekday} className='text-center text-muted fw-bold fs-8 py-1'>
                {weekday}
              </div>
            ))}

            {calendarDays.map(day => {
              const dayIso = toIso(day)
              const inCurrentMonth = day.month() === viewMonth.month()
              const disabled = maxIso != null && dayIso > maxIso
              const edge = isEdge(dayIso)
              const inRange = isInRange(dayIso)

              return (
                <button
                  key={dayIso}
                  type='button'
                  disabled={disabled}
                  onMouseEnter={() => pendingStart && setHoverDay(dayIso)}
                  onClick={() => handleDayClick(dayIso)}
                  className={clsx(
                    'btn btn-sm p-0 fw-semibold',
                    edge && 'btn-primary text-white',
                    !edge && inRange && 'bg-light-primary text-primary',
                    !edge && !inRange && 'btn-active-light-primary',
                    !edge && !inRange && (inCurrentMonth ? 'text-gray-800' : 'text-muted'),
                    disabled && 'opacity-25'
                  )}
                  style={{ height: 34, borderRadius: 6 }}>
                  {day.date()}
                </button>
              )
            })}
          </div>

          <div className='d-flex justify-content-end mt-3'>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => {
                setOpen(false)
                setPendingStart(null)
                setHoverDay(null)
              }}>
              ปิด
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default DateRangePicker
